import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  Divider,
  CircularProgress
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Home as HomeIcon
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import usePaymentPolling from '../../hooks/usePaymentPolling'
import PixPayment from '../../components/Payment/PixPayment'
import { refreshPixQrCode } from '../../services/paymentService'

const Checkout = () => {
  const { paymentId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()

  // Dados passados pela navegação (opcional)
  const locationState = location.state || {}
  const { propertyId, planName } = locationState

  const [refreshingPix, setRefreshingPix] = useState(false)

  const handleRefreshPix = async () => {
    if (!payment?.id) return
    setRefreshingPix(true)
    try {
      await refreshPixQrCode(payment.id, token)
      // O polling vai capturar o novo QR Code automaticamente na próxima verificação
    } catch (e) {
      console.error('Erro ao regenerar QR Code:', e)
    } finally {
      setRefreshingPix(false)
    }
  }

  // Hook de polling para verificar status do pagamento
  const { payment, loading, error, isConfirmed, isPolling } = usePaymentPolling(
    paymentId,
    token,
    5000, // Verifica a cada 5 segundos
    true  // Polling ativo
  )

  // Redireciona para sucesso quando confirmado
  useEffect(() => {
    if (isConfirmed && payment) {
      setTimeout(() => {
        navigate('/payment-success', {
          state: {
            payment,
            propertyId,
            planName: planName || payment.boost?.plan?.name
          }
        })
      }, 2000) // Aguarda 2 segundos para o usuário ver a confirmação
    }
  }, [isConfirmed, payment, navigate, propertyId, planName])

  if (loading && !payment) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box mb={3}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Checkout
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete o pagamento para ativar seu impulsionamento
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Informações do Plano */}
        {payment && (
          <Box mb={3}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Resumo do Pedido
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Plano:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {planName || payment.boost?.plan?.name || 'Impulsionamento'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Método:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {payment.payment_method_display || 'PIX'}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body1" fontWeight={600}>
                  Total:
                </Typography>
                <Typography variant="h6" color="primary" fontWeight={700}>
                  R$ {parseFloat(payment.amount).toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Componente de Pagamento PIX */}
        {payment?.payment_method === 'pix' && (
          <PixPayment
            payment={payment}
            isConfirmed={isConfirmed}
            isPolling={isPolling}
            onRefreshPix={handleRefreshPix}
            refreshing={refreshingPix}
          />
        )}

        {/* Outros métodos de pagamento podem ser adicionados aqui */}
        {payment?.payment_method === 'boleto' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Boleto bancário - Em breve
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Botões de Ação */}
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            disabled={isConfirmed}
          >
            Voltar
          </Button>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
          >
            Ir para Home
          </Button>
        </Box>

        {/* Informações Adicionais */}
        <Box mt={3}>
          <Alert severity="info">
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Dúvidas sobre o pagamento?
            </Typography>
            <Typography variant="body2">
              • O pagamento via PIX é processado em tempo real
            </Typography>
            <Typography variant="body2">
              • Você receberá uma confirmação assim que o pagamento for identificado
            </Typography>
            <Typography variant="body2">
              • Seu impulsionamento será ativado automaticamente
            </Typography>
          </Alert>
        </Box>
      </Paper>
    </Container>
  )
}

export default Checkout
